import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

const ScopeEditorForm = ({ userData, resources, onClose, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    userId: '',
    userType: '',
    roleId: '',
    roleName: '',
    resourcePermissions: {} // { resource_name: 'cruds' }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [categoryExpanded, setCategoryExpanded] = useState({});

  // CRUDS permission presets
  const CRUDS_PRESETS = [
    { value: '', label: 'No Access', description: 'No permissions' },
    { value: 'r', label: 'Read-Only (R)', description: 'View only' },
    { value: 'cr', label: 'Limited (CR)', description: 'Create, Read' },
    { value: 'ru', label: 'Edit Only (RU)', description: 'Read, Update' },
    { value: 'cru', label: 'Staff (CRU)', description: 'Create, Read, Update' },
    { value: 'crud', label: 'Standard (CRUD)', description: 'Create, Read, Update, Delete' },
    { value: 'cruds', label: 'Full Access (CRUDS)', description: 'All permissions including special/admin ops' },
  ];

  // Parse scope string into permissions object
  const parseScopeString = (scopeString) => {
    if (!scopeString) return {};

    const permissions = {};
    const scopes = scopeString.split(' ');

    scopes.forEach(scope => {
      if (scope.includes('.')) {
        const [resource, cruds] = scope.split('.', 2);
        permissions[resource] = cruds;
      }
    });

    return permissions;
  };

  // Build scope string from permissions object
  const buildScopeString = (permissions) => {
    const scopes = [];
    Object.entries(permissions).forEach(([resource, cruds]) => {
      if (cruds && cruds.length > 0) {
        scopes.push(`${resource}.${cruds}`);
      }
    });
    return scopes.sort().join(' ');
  };

  // Initialize form data from userData prop
  useEffect(() => {
    if (userData) {
      const scopeString = userData.role?.metadata?.scopes || '';
      const permissions = parseScopeString(scopeString);

      setFormData({
        userId: userData.id || '',
        userType: userData.user_type || '',
        roleId: userData.role?.id || '',
        roleName: userData.role?.name || '',
        resourcePermissions: permissions
      });

      // Expand all categories by default
      const expanded = {};
      resources.forEach(r => {
        expanded[r.category] = true;
      });
      setCategoryExpanded(expanded);
    }
  }, [userData, resources]);

  const handlePermissionChange = (resourceName, cruds) => {
    setFormData(prev => ({
      ...prev,
      resourcePermissions: {
        ...prev.resourcePermissions,
        [resourceName]: cruds
      }
    }));
  };

  const toggleCategory = (category) => {
    setCategoryExpanded(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Build new scope string
      const newScopeString = buildScopeString(formData.resourcePermissions);

      // Update role metadata with new scopes
      const updatePayload = {
        metadata: {
          ...(userData.role?.metadata || {}),
          scopes: newScopeString
        }
      };

      // Update the role via API
      const response = await adminApi.patch(
        `/admin/roles/${formData.roleId}/`,
        updatePayload
      );

      if (response.data && response.data.success !== false) {
        setSuccess('Scopes updated successfully! Users with this role will need to log in again for changes to take effect.');
        setTimeout(() => {
          onSaveSuccess && onSaveSuccess('Role scopes updated successfully');
        }, 1500);
      } else {
        setError(response.data?.message || 'Failed to update scopes');
      }
    } catch (err) {
      console.error('Error updating scopes:', err);
      setError(err.response?.data?.message || err.message || 'Error updating scopes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove ALL scopes for this role? This will revoke access to all resources for users with this role.')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Clear all scopes
      const updatePayload = {
        metadata: {
          ...(userData.role?.metadata || {}),
          scopes: ''
        }
      };

      const response = await adminApi.patch(
        `/admin/roles/${formData.roleId}/`,
        updatePayload
      );

      if (response.data && response.data.success !== false) {
        setSuccess('All scopes removed successfully!');
        setTimeout(() => {
          onSaveSuccess && onSaveSuccess('Role scopes cleared');
        }, 1500);
      } else {
        setError(response.data?.message || 'Failed to clear scopes');
      }
    } catch (err) {
      console.error('Error clearing scopes:', err);
      setError(err.response?.data?.message || err.message || 'Error clearing scopes');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = (preset) => {
    if (!window.confirm(`Apply "${preset.label}" preset to ALL resources?\n\nThis will set all resources to: ${preset.description}`)) {
      return;
    }

    const newPermissions = {};
    resources.forEach(resource => {
      if (preset.value) {
        newPermissions[resource.resource] = preset.value;
      }
    });

    setFormData(prev => ({
      ...prev,
      resourcePermissions: newPermissions
    }));
  };

  // Group resources by category
  const resourcesByCategory = resources.reduce((acc, resource) => {
    const category = resource.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(resource);
    return acc;
  }, {});

  const roleName = formData.roleName || userData.role?.name || 'Unknown Role';
  const roleSlug = userData.role?.slug || 'N/A';
  const roleCategory = formData.userType || userData.role?.category || 'N/A';
  const roleOrg = userData.role?.organization_name || 'Global';

  return (
    <div className="scope-editor-form p-3">
      {/* Role Info */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="text-primary mb-3">Role Information</h6>
          <div className="row">
            <div className="col-md-6">
              <p className="mb-1"><strong>Role Name:</strong> {roleName}</p>
              <p className="mb-1"><strong>Slug:</strong> <code>{roleSlug}</code></p>
            </div>
            <div className="col-md-6">
              <p className="mb-1"><strong>Category:</strong> <span className="badge bg-primary">{roleCategory}</span></p>
              <p className="mb-1"><strong>Organization:</strong> {roleOrg}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </div>
      )}

      {/* Quick Presets */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="text-primary mb-2">Quick Presets</h6>
          <small className="text-muted d-block mb-3">
            Apply a permission preset to all resources at once
          </small>
          <div className="d-flex flex-wrap gap-2">
            {CRUDS_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => handleSelectPreset(preset)}
                disabled={loading}
                title={preset.description}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Resource Permissions */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="text-primary mb-3">Resource Permissions</h6>
          <small className="text-muted d-block mb-3">
            Set CRUDS permissions for each resource (C=Create, R=Read, U=Update, D=Delete, S=Special/Admin)
          </small>

          {Object.entries(resourcesByCategory).map(([category, categoryResources]) => (
            <div key={category} className="mb-3">
              {/* Category Header */}
              <div
                className="d-flex align-items-center justify-content-between bg-light p-2 border rounded cursor-pointer"
                onClick={() => toggleCategory(category)}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="mb-0">
                  <i className={`fas fa-chevron-${categoryExpanded[category] ? 'down' : 'right'} me-2`}></i>
                  {category}
                  <span className="badge bg-secondary ms-2">{categoryResources.length}</span>
                </h6>
              </div>

              {/* Category Resources */}
              {categoryExpanded[category] && (
                <div className="mt-2">
                  {categoryResources.map((resource) => {
                    const currentPermission = formData.resourcePermissions[resource.resource] || '';

                    return (
                      <div key={resource.resource} className="row align-items-center mb-2 pb-2 border-bottom">
                        <div className="col-md-4">
                          <strong>{resource.resource}</strong>
                          {resource.description && (
                            <small className="d-block text-muted">{resource.description}</small>
                          )}
                        </div>
                        <div className="col-md-8">
                          <select
                            className="form-select form-select-sm"
                            value={currentPermission}
                            onChange={(e) => handlePermissionChange(resource.resource, e.target.value)}
                            disabled={loading}
                          >
                            {CRUDS_PRESETS.map((preset) => (
                              <option key={preset.value} value={preset.value}>
                                {preset.label} - {preset.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {resources.length === 0 && (
            <div className="text-center text-muted py-3">
              <i className="fas fa-info-circle me-2"></i>
              No resources available. Resources are auto-discovered from backend views.
            </div>
          )}
        </div>
      </div>

      {/* Current Scope String Preview */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="text-primary mb-2">Scope String Preview</h6>
          <small className="text-muted d-block mb-2">
            This will be stored in the role metadata and included in the JWT
          </small>
          <pre className="bg-light p-2 rounded mb-0" style={{ fontSize: '0.875rem' }}>
            {buildScopeString(formData.resourcePermissions) || '<empty - no scopes>'}
          </pre>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={loading || !formData.roleId}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-1"></span>
              Saving...
            </>
          ) : (
            <>Save Scopes</>
          )}
        </button>
        <button
          type="button"
          className="btn btn-outline-danger"
          onClick={handleDelete}
          disabled={loading || !formData.roleId}
        >Clear All Scopes
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
      </div>

      {!formData.roleId && (
        <div className="alert alert-warning mt-3" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Role ID not found. Cannot manage scopes.
        </div>
      )}
    </div>
  );
};

export default ScopeEditorForm;
